import type { AuthSession } from './types.ts'
import {
  backendRuntimeConfig,
  resolveApiUrl,
  type BackendRuntimeConfig
} from './runtimeConfig.ts'

export type AuthSessionFailureKind =
  | 'unauthorized'
  | 'forbidden'
  | 'access_redirect'
  | 'cors'
  | 'network'
  | 'server'
  | 'invalid_response'

export type AuthSessionFailure = {
  kind: AuthSessionFailureKind
  status: number | null
}

export type AuthSessionCheckResult =
  | { status: 'preview' }
  | { status: 'ready'; session: AuthSession }
  | { status: 'failure'; failure: AuthSessionFailure }

export type AuthGatePresentation = {
  eyebrow: string
  title: string
  message: string
  actionLabel: string
}

type AuthSessionFetch = (
  input: RequestInfo | URL,
  init?: RequestInit
) => Promise<Response>

export type CheckAuthSessionOptions = {
  fetchImpl?: AuthSessionFetch
  online?: boolean
  pageHref?: string
  signal?: AbortSignal
}

export async function checkAuthSession(
  config: BackendRuntimeConfig = backendRuntimeConfig,
  options: CheckAuthSessionOptions = {}
): Promise<AuthSessionCheckResult> {
  if (!config.enabled || !config.apiBaseUrl) return { status: 'preview' }

  const sessionUrl = resolveApiUrl('/v1/auth/session', config)
  const fetchImpl = options.fetchImpl ?? fetch
  const traceId = crypto.randomUUID()
  try {
    const response = await fetchImpl(sessionUrl, {
      method: 'GET',
      credentials: 'include',
      cache: 'no-store',
      headers: {
        'Content-Type': 'application/json',
        'X-Trace-Id': traceId
      },
      signal: options.signal
    })
    if (response.status === 401) return failure('unauthorized', 401)
    if (response.status === 403) return failure('forbidden', 403)
    if (looksLikeAccessRedirect(response, sessionUrl)) {
      return failure('access_redirect', response.status || null)
    }
    if (!response.ok) return failure('server', response.status)

    const contentType = response.headers.get('Content-Type')?.toLowerCase() ?? ''
    if (contentType.includes('text/html')) {
      return failure('access_redirect', response.status)
    }
    const payload: unknown = await response.json().catch(() => null)
    const session = parseAuthSession(payload)
    return session
      ? { status: 'ready', session }
      : failure('invalid_response', response.status)
  } catch (error) {
    if (options.signal?.aborted) throw error
    const online = options.online ?? (typeof navigator === 'undefined' || navigator.onLine)
    if (!online) return failure('network', null)
    const pageHref = options.pageHref
      ?? (typeof window === 'undefined' ? undefined : window.location.href)
    return failure(isCrossOrigin(sessionUrl, pageHref) ? 'cors' : 'network', null)
  }
}

export function parseAuthSession(value: unknown): AuthSession | null {
  if (!isRecord(value)) return null
  const email = value.email
  if (
    value.authenticated !== true
    || typeof value.auth_required !== 'boolean'
    || (value.mode !== 'disabled' && value.mode !== 'cloudflare_access')
    || value.auth_required !== (value.mode === 'cloudflare_access')
    || value.tenant_scope !== 'fixed_runtime'
    || !nonEmptyString(value.tenant_id)
    || !nonEmptyString(value.subject)
    || (email !== null && !nonEmptyString(email))
  ) return null
  return {
    authenticated: true,
    auth_required: value.auth_required,
    mode: value.mode,
    tenant_scope: value.tenant_scope,
    tenant_id: value.tenant_id,
    subject: value.subject,
    email
  }
}

export function authFailureFromStatus(status: 401 | 403): AuthSessionFailure {
  return {
    kind: status === 403 ? 'forbidden' : 'unauthorized',
    status
  }
}

export function authGatePresentation(
  failureValue: AuthSessionFailure
): AuthGatePresentation {
  switch (failureValue.kind) {
    case 'unauthorized':
      return {
        eyebrow: 'ACCESS 401',
        title: '需要登录才能启动终端',
        message: '请先打开 API 会话页完成 Cloudflare Access 登录，然后返回重试。终端不会保存访问令牌。',
        actionLabel: '打开登录 / 会话页'
      }
    case 'forbidden':
      return {
        eyebrow: 'ACCESS 403',
        title: '当前身份无权访问',
        message: '已到达会话服务，但当前 Cloudflare Access 身份不在允许策略中。请切换授权账号或联系管理员。',
        actionLabel: '查看会话身份'
      }
    case 'access_redirect':
      return {
        eyebrow: 'ACCESS LOGIN',
        title: 'Cloudflare Access 登录待完成',
        message: '会话请求收到了登录页而不是 JSON。请在新标签完成登录，然后回到此处重试。',
        actionLabel: '打开登录 / 会话页'
      }
    case 'cors':
      return {
        eyebrow: 'CORS / ACCESS',
        title: '浏览器无法读取跨域会话',
        message: '请先打开会话页完成 Access 登录。如果已登录，请检查 API CORS 是否允许当前终端 Origin 携带 Cookie。',
        actionLabel: '打开 API 会话页'
      }
    case 'network':
      return {
        eyebrow: 'NETWORK',
        title: '无法连接终端服务',
        message: '请检查网络、API 域名和 Cloudflare Tunnel 状态，恢复后重试。',
        actionLabel: '打开 API 会话页'
      }
    case 'server':
      return {
        eyebrow: `SESSION ${failureValue.status ?? 'ERROR'}`,
        title: '会话服务暂不可用',
        message: '服务端已响应，但会话检查失败。请检查 API 版本和运行状态。',
        actionLabel: '查看 API 会话页'
      }
    case 'invalid_response':
      return {
        eyebrow: 'SESSION CONTRACT',
        title: '会话响应不符合约定',
        message: '服务端返回了非预期响应；为避免未授权请求风暴，终端已停止加载功能模块。',
        actionLabel: '查看 API 会话页'
      }
  }
}

function failure(
  kind: AuthSessionFailureKind,
  status: number | null
): AuthSessionCheckResult {
  return { status: 'failure', failure: { kind, status } }
}

function looksLikeAccessRedirect(response: Response, sessionUrl: string): boolean {
  if (response.redirected) return true
  if (!response.url) return false
  try {
    return new URL(response.url).origin !== new URL(sessionUrl).origin
  } catch {
    return false
  }
}

function isCrossOrigin(targetUrl: string, pageHref: string | undefined): boolean {
  if (!pageHref) return false
  try {
    return new URL(targetUrl).origin !== new URL(pageHref).origin
  } catch {
    return false
  }
}

function nonEmptyString(value: unknown): value is string {
  return typeof value === 'string' && Boolean(value.trim())
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value)
}
